/**
 * 下载管理（路由 /myse/downloads）
 * UI 严格对齐 web后台.html v2 PAGE: 下载管理
 *
 * 区域：
 *  1. 顶部 header：标题"下载管理" + 副标题
 *  2. 表格 7 列：导出名称 / 导出模块(软Tag) / 导出条数 / 状态(Tag) / 操作人 / 开始时间 / 操作(下载/等待中/失败)
 *  3. 底部说明："导出文件保留 30 天，过期后将自动清理。"
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Layout, Pagination, Spin, Table, message } from 'antd'
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useAppDispatch } from '../../../app/hooks'
import { fetchAllExports } from './api'
import {
  DownloadModule,
  DownloadRecord,
  DownloadStatus,
  MODULE_COLORS,
  MODULE_MAP,
  STATUS_LABEL
} from './types'
import StatusTag from '../shared/StatusTag'
import '../shared/styles.scss'
import './index.scss'

const { Content } = Layout

const DownloadsPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const [list, setList] = useState<DownloadRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchAllExports(dispatch, { page_size: 200 })
      setList(data)
    } catch (e) {
      message.error((e as Error)?.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    load()
  }, [load])

  // 等待中(status=0)的任务后端还在生成,前端轮询直到全部完成或失败
  const hasPending = useMemo(() => list.some((r) => r.status === 0), [list])
  useEffect(() => {
    if (!hasPending) return
    const t = setInterval(() => {
      load()
    }, 5000)
    return () => clearInterval(t)
  }, [hasPending, load])

  const columns = useMemo(
    () => [
      {
        title: '导出名称',
        dataIndex: 'name',
        key: 'name',
        width: 280,
        ellipsis: true,
        render: (text: string) => (
          <span style={{ color: '#1F2937', fontWeight: 500 }}>{text}</span>
        )
      },
      {
        title: '导出模块',
        dataIndex: 'module',
        key: 'module',
        width: 110,
        render: (m: DownloadModule) => {
          const meta = MODULE_COLORS[m]
          return (
            <span
              style={{
                display: 'inline-flex',
                padding: '2px 8px',
                borderRadius: 4,
                background: meta.bg,
                color: meta.color,
                fontSize: 12
              }}
            >
              {MODULE_MAP[m] || m}
            </span>
          )
        }
      },
      {
        title: '导出条数',
        dataIndex: 'row_count',
        key: 'row_count',
        width: 90,
        render: (n: number | null) =>
          n != null ? (
            <span>
              <span
                style={{
                  fontWeight: 600,
                  color: '#1F2937',
                  fontFamily: 'Inter, system-ui'
                }}
              >
                {n.toLocaleString()}
              </span>
              <span style={{ color: '#9CA3AF', marginLeft: 4 }}>条</span>
            </span>
          ) : (
            <span style={{ color: '#D1D5DB' }}>—</span>
          )
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 80,
        render: (s: DownloadStatus) => {
          if (s === 1)
            return (
              <StatusTag label={STATUS_LABEL[1]} color="#10A66B" bg="#E7F8EF" />
            )
          if (s === 0)
            return (
              <StatusTag label={STATUS_LABEL[0]} color="#FA8C16" bg="#FFF7E6" />
            )
          return (
            <StatusTag label={STATUS_LABEL[2]} color="#E84C4C" bg="#FEECEC" />
          )
        }
      },
      {
        title: '操作人',
        dataIndex: 'operator',
        key: 'operator',
        width: 80
      },
      {
        title: '开始时间',
        dataIndex: 'start_time',
        key: 'start_time',
        width: 160,
        render: (text: string) => (
          <span style={{ fontFamily: 'Inter, system-ui', color: '#6B7280' }}>
            {text || '-'}
          </span>
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: 'right' as const,
        render: (_: any, row: DownloadRecord) => {
          if (row.status === 1) {
            return (
              <a
                href={row.download_url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#3B7DEB',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <DownloadOutlined /> 下载
              </a>
            )
          }
          if (row.status === 0) {
            return <span style={{ color: '#9CA3AF' }}>等待中…</span>
          }
          return (
            <span
              style={{ color: '#D1D5DB', cursor: 'not-allowed' }}
              title={row.error_msg}
            >
              <DownloadOutlined /> 下载
            </span>
          )
        }
      }
    ],
    []
  )

  return (
    <Layout className="downloads-page">
      <Content style={{ padding: 24 }}>
        {/* Header */}
        <div className="myse-page-header">
          <div>
            <h1 className="myse-page-title">下载管理</h1>
            <div className="myse-page-subtitle">
              查看和下载各模块导出的数据文件，导出任务完成后可随时下载。
            </div>
          </div>
        </div>

        {/* 表格 */}
        <div className="mingyang-table-wrapper">
          <Spin spinning={loading} delay={120}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={list}
              scroll={{ x: 1080 }}
              pagination={false}
              size="middle"
            />
          </Spin>
          <div className="mingyang-table-footer mingyang-table-footer--split">
            <span className="downloads-footnote">
              <ExclamationCircleOutlined
                style={{ color: '#F59E0B', marginRight: 6 }}
              />
              导出文件保留 30 天，过期后将自动清理。
            </span>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={list.length}
              showSizeChanger
              pageSizeOptions={['20', '50', '100']}
              showTotal={(n) => `共 ${n} 条`}
              onChange={(p, s) => {
                setPage(p)
                setPageSize(s || 20)
              }}
              onShowSizeChange={(_, s) => {
                setPage(1)
                setPageSize(s)
              }}
            />
          </div>
        </div>
      </Content>
    </Layout>
  )
}

export default DownloadsPage
